import React, { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { TFunction, useTranslation } from '@lyearn/i18n';
import {
  ArrowRightFill,
  CheckmarkCircleFill,
  CheckmarkFill,
  Mobile2Outline,
  MonitorOutline,
  PauseOutline,
  PlayFill,
} from '@lyearn/icons';
import { cn } from '@lyearn/style';
import { Box, Button, IconButton, Loader, Select, Stack, Typography } from '@lyearn/ui';
import AsyncView from '@/components/AsyncView';
import { Fields, FieldType } from '@/components/Form';
import Form, { FormProps } from '@/components/Form';
import { useFormFieldValue } from '@/components/Form';
import { getSelectOptionLabel } from '@/components/Form/components/Select/getOptionLabel';
import { Image } from '@/components/Image';
import RadioGroup from '@/components/RadioGroup';
import {
  CharacterAvatarType,
  CharacterVoiceType,
  VideoCharacterProviderEnum,
  VideoGenerationInputType,
} from '@/types/schema';

import AvatarGridView from './AvatarSelectionModal';
import { useVideoCharacterAvatars } from './useVideoCharacterAvatars';
import { useVideoCharacterVoices } from './useVideoCharacterVoices';

export interface GenerateVideoFormValues {
  videoType: string;
  avatar: string;
  language: string;
  voice: string;
  videoOrientation: string;
  background: string;
  /** Display-only: preview video for selected avatar */
  preview?: string;
}

const getSelectValue = (value: string | { value?: string } | null | undefined) => {
  if (typeof value === 'string') {
    return value;
  }

  return value?.value || '';
};

const getAvatarByValue = (avatars: CharacterAvatarType[], avatarValue: string) => {
  return avatars.find((avatar) => avatar.avatar_id === avatarValue);
};

const getVoiceOptions = ({
  voices,
  languageValue,
  genderValue,
}: {
  voices: CharacterVoiceType[];
  languageValue?: string;
  genderValue?: string | null;
}) => {
  return voices.filter((voice) => {
    const languageMatch = languageValue ? voice.language === languageValue : true;
    const genderMatch = genderValue ? voice.gender === genderValue : true;
    return languageMatch && genderMatch;
  });
};

const getDefaultVoice = ({
  voices,
  languageValue,
  genderValue,
  preferredVoiceValue,
}: {
  voices: CharacterVoiceType[];
  languageValue?: string;
  genderValue?: string | null;
  preferredVoiceValue?: string;
}) => {
  const matchedVoices = getVoiceOptions({ voices, languageValue, genderValue });
  const preferredVoice = preferredVoiceValue
    ? voices.find((voiceOption) => voiceOption.voice_id === preferredVoiceValue)
    : undefined;
  const languageOnlyVoices = getVoiceOptions({ voices, languageValue });
  const genderOnlyVoices = getVoiceOptions({ voices, genderValue });

  if (
    preferredVoiceValue &&
    matchedVoices.some((voiceOption) => voiceOption.voice_id === preferredVoiceValue)
  ) {
    return preferredVoiceValue;
  }

  if (matchedVoices[0]?.voice_id) {
    return matchedVoices[0].voice_id;
  }

  if (preferredVoice?.voice_id) {
    return preferredVoice.voice_id;
  }

  if (languageOnlyVoices[0]?.voice_id) {
    return languageOnlyVoices[0].voice_id;
  }

  if (genderOnlyVoices[0]?.voice_id) {
    return genderOnlyVoices[0].voice_id;
  }

  return voices[0]?.voice_id || '';
};

const BACKGROUND_CONFIG = [
  {
    background: '#415361',
    foreground: '#dadada',
    type: 'color',
  },
  {
    background: '#3C92D5',
    foreground: '#1e1e1e',
    type: 'color',
  },
  {
    background: '#6D6DD9',
    foreground: '#1e1e1e',
    type: 'color',
  },
  {
    background: '#CC6EDD',
    foreground: '#1e1e1e',
    type: 'color',
  },
  {
    background: '#DB70A0',
    foreground: '#1e1e1e',
    type: 'color',
  },
  {
    background: '#4BCEDD',
    foreground: '#1e1e1e',
    type: 'color',
  },
  {
    background: '#88CE9B',
    foreground: '#1e1e1e',
    type: 'color',
  },
];

interface GetFormFieldsProps {
  t: TFunction;
  avatars: CharacterAvatarType[];
  voices: CharacterVoiceType[];
  isAvatarsFetching: boolean;
  isVoicesFetching: boolean;
  onShowAvatarSelection: () => void;
  avatarChangeHandlerRef: React.MutableRefObject<((id: string) => void) | null>;
  currentAvatarIdRef: React.MutableRefObject<string>;
}

const getFormFields = ({
  t,
  avatars,
  voices,
  isAvatarsFetching,
  isVoicesFetching,
  onShowAvatarSelection,
  avatarChangeHandlerRef,
  currentAvatarIdRef,
}: GetFormFieldsProps): Fields<GenerateVideoFormValues> => {
  const isAvatarSelectionDisabled = isAvatarsFetching || avatars.length === 0;

  return {
    preview: {
      name: 'preview',
      label: t('Course:AiVideoGeneration.Preview'),
      required: true,
      type: FieldType.Custom,
      Component: () => {
        const videoType = useFormFieldValue('videoType');
        const avatar = useFormFieldValue('avatar');
        const avatarConfig = getAvatarByValue(avatars, avatar);

        if (videoType === 'voiceOver') return null;

        if (isAvatarsFetching && !avatarConfig) {
          return (
            <Stack alignItems="center" className="h-[240px] w-full" justifyContent="center">
              <Loader />
            </Stack>
          );
        }

        return (
          <Stack
            alignItems="center"
            className="relative w-full overflow-hidden rounded-16 bg-[#000000] "
            direction="col">
            <Stack
              alignItems="center"
              className="w-[100%] overflow-hidden "
              justifyContent="center"
              style={{ aspectRatio: '16/9' }}>
              {avatarConfig?.preview_video_url ? (
                <video
                  key={avatar}
                  playsInline
                  className="h-full w-full object-cover"
                  controls={true}
                  src={avatarConfig.preview_video_url}
                />
              ) : null}
            </Stack>
            {/* <span className="absolute right-8 top-8 rounded-8 bg-[rgba(0,0,0,0.6)] px-8 py-4 text-12 font-medium text-white">
              Ratio 16:9
            </span> */}
          </Stack>
        );
      },
    },
    // videoType: {
    //   name: 'videoType',
    //   label: '',
    //   required: true,
    //   type: FieldType.Custom,
    //   Component: ({ value, onChange }) => {
    //     const modes = [
    //       ...(SHOW_VOICE_OVER_OPTION
    //         ? [
    //             {
    //               id: 'voiceOver',
    //               label: t('Course:AiVideoGeneration.VoiceOver'),
    //               description: t('Course:AiVideoGeneration.VoiceOverDescription'),
    //             },
    //           ]
    //         : []),
    //       {
    //         id: 'avatarNarration',
    //         label: t('Course:AiVideoGeneration.AvatarAndVoice'),
    //         description: t('Course:AiVideoGeneration.AvatarAndVoiceDescription'),
    //       },
    //     ];

    //     return (
    //       <Stack direction="row" gap={12}>
    //         {modes.map((mode) => {
    //           const isSelected = value === mode.id;
    //           return (
    //             <Stack
    //               key={mode.id}
    //               className={cn(
    //                 'flex-1 cursor-pointer rounded-[12px] border p-12',
    //                 isSelected ? 'border-text-primary' : 'border-border',
    //               )}
    //               direction="col"
    //               gap={2}
    //               role="button"
    //               tabIndex={0}
    //               onClick={() => onChange(mode.id)}
    //               onKeyDown={(e: React.KeyboardEvent) => e.key === 'Enter' && onChange(mode.id)}>
    //               <Stack alignItems="start" direction="row" justifyContent="between">
    //                 <Typography className="font-medium" variant="body-short-01">
    //                   {mode.label}
    //                 </Typography>
    //                 {isSelected ? (
    //                   <CheckmarkCircleFill height={24} width={24} />
    //                 ) : (
    //                   <RadioButtonOffOutline
    //                     className="text-icon-placeholder"
    //                     height={20}
    //                     width={20}
    //                   />
    //                 )}
    //               </Stack>
    //               <Typography color="text-text-secondary" variant="body-short-02">
    //                 {mode.description}
    //               </Typography>
    //             </Stack>
    //           );
    //         })}
    //       </Stack>
    //     );
    //   },
    // },
    avatar: {
      name: 'avatar',
      label: '',
      required: true,
      type: FieldType.Custom,
      Component: ({ value, onChange, setFieldValue }) => {
        const videoType = useFormFieldValue('videoType');
        const changeHandler = useCallback(
          (avatarValueInput: string | { value?: string } | null | undefined) => {
            const avatarValue = getSelectValue(avatarValueInput);
            const selectedAvatar = getAvatarByValue(avatars, avatarValue);
            const nextVoice = getDefaultVoice({
              voices,
              genderValue: selectedAvatar?.gender,
              preferredVoiceValue: selectedAvatar?.default_voice_id ?? undefined,
            });

            onChange(avatarValue);
            setFieldValue('voice', nextVoice);
          },
          [onChange, setFieldValue],
        );

        // Latest-ref pattern: keep refs in sync with current values every render
        avatarChangeHandlerRef.current = changeHandler;
        currentAvatarIdRef.current = value;

        const VISIBLE_COUNT = 5;
        const firstFive = avatars.slice(0, VISIBLE_COUNT);
        const selectedInFirstFive = firstFive.some((a) => a.avatar_id === value);
        const selectedAvatarObj = !selectedInFirstFive ? getAvatarByValue(avatars, value) : null;
        const visibleAvatars = selectedAvatarObj
          ? [selectedAvatarObj, ...firstFive.slice(0, VISIBLE_COUNT - 1)]
          : firstFive;

        if (videoType === 'voiceOver') return null;

        if (isAvatarsFetching && avatars.length === 0) {
          return (
            <Stack alignItems="center" className="h-96 w-full" justifyContent="center">
              <Loader />
            </Stack>
          );
        }

        return (
          <>
            {/* Label row with "See all" button */}
            <Stack alignItems="center" className="mb-8" direction="row" justifyContent="between">
              <Typography className="font-medium" color="text-text-primary" variant="body-short-01">
                {t('Course:AiVideoGeneration.Avatar')}
              </Typography>
              {avatars.length > VISIBLE_COUNT ? (
                <Button
                  EndIcon={ArrowRightFill}
                  size="x-small"
                  variant="ghost"
                  onClick={onShowAvatarSelection}>
                  {t('Common:Actions.SeeAll')}
                </Button>
              ) : null}
            </Stack>

            {/* 1-row avatar strip */}
            <Stack className="flex-nowrap gap-12 overflow-hidden" direction="row">
              {visibleAvatars.map((avatar) => {
                const isSelected = value === avatar.avatar_id;

                return (
                  <Box
                    key={avatar.avatar_id}
                    className={cn(
                      'relative shrink-0 cursor-pointer overflow-hidden rounded-12 border',
                      isSelected ? 'border-2 border-text-primary' : 'border-border',
                    )}
                    style={{ width: 96, height: 100 }}
                    onClick={() => !isAvatarSelectionDisabled && changeHandler(avatar.avatar_id)}>
                    <Image
                      alt={avatar.avatar_name}
                      className="h-full w-full object-cover object-top"
                      height={100}
                      src={avatar.preview_image_url || ''}
                      width={100}
                    />
                    {isSelected ? (
                      <Box className="absolute right-4 top-4 flex h-24 w-24 items-center justify-center rounded-full bg-bg-primary">
                        <CheckmarkCircleFill height={24} width={24} />
                      </Box>
                    ) : null}
                  </Box>
                );
              })}
            </Stack>
          </>
        );
      },
    },
    voice: {
      name: 'voice',
      label: t('Course:AiVideoGeneration.Voice'),
      required: true,
      type: FieldType.Custom,
      Component: ({ value, onChange, setFieldValue }) => {
        const selectedAvatarId = useFormFieldValue('avatar');
        const videoType = useFormFieldValue('videoType');
        const audioRef = useRef<HTMLAudioElement | null>(null);
        const [playingVoiceId, setPlayingVoiceId] = useState<string | null>(null);

        const selectedAvatarGender = getAvatarByValue(avatars, selectedAvatarId)?.gender;
        const genderVoices = getVoiceOptions({ voices, genderValue: selectedAvatarGender });
        const voiceOptions = (genderVoices.length > 0 ? genderVoices : voices).map((v) => ({
          label: v.name,
          value: v.voice_id,
          voice: v,
        }));

        const resolvedValue =
          voiceOptions.find((o) => o.value === value)?.value || voiceOptions[0]?.value || '';

        // Only auto-correct when value is genuinely absent from the voices list entirely
        // (e.g. a stale/deleted voice ID from saved attrs). Do NOT override when value is a
        // valid voice that is temporarily filtered out — avatar/language change handlers
        // already set the correct voice explicitly, and overriding here causes the wrong
        // voice to win when selectedAvatarId and value land in different render cycles.
        const isValueValidGlobally = Boolean(value && voices.some((v) => v.voice_id === value));

        useEffect(() => {
          if (!isValueValidGlobally && value !== resolvedValue) {
            setFieldValue('voice', resolvedValue);
          }
        }, [isValueValidGlobally, resolvedValue, setFieldValue, value]);

        useEffect(() => {
          return () => {
            audioRef.current?.pause();
          };
        }, []);

        const handlePreviewClick = useCallback(
          (e: React.MouseEvent, voiceId: string, previewUrl?: string | null) => {
            e.stopPropagation();
            e.preventDefault();
            if (!previewUrl) return;
            if (playingVoiceId === voiceId) {
              audioRef.current?.pause();
              setPlayingVoiceId(null);
              return;
            }
            audioRef.current?.pause();
            const audio = new Audio(previewUrl);
            audioRef.current = audio;
            audio.play();
            setPlayingVoiceId(voiceId);
            audio.onended = () => setPlayingVoiceId(null);
          },
          [playingVoiceId],
        );

        // Voice Over mode: 2-column card grid with play/pause preview
        if (videoType === 'voiceOver') {
          return (
            <Stack className="flex-wrap gap-12" direction="row">
              {voiceOptions.map((option) => {
                const isSelected = value === option.value;
                const isPlaying = playingVoiceId === option.value;
                const createdBy = option.voice?.createdBy;
                const createdByName = createdBy
                  ? `${createdBy.firstName ?? ''} ${createdBy.lastName ?? ''}`.trim()
                  : null;

                return (
                  <Stack
                    key={option.value}
                    alignItems="center"
                    className={cn(
                      'relative w-[calc(50%-6px)] cursor-pointer rounded-[12px] border p-12',
                      isSelected ? 'border-text-primary' : 'border-border',
                    )}
                    direction="row"
                    gap={12}
                    onClick={() => onChange(option.value)}>
                    {/* Play / pause preview button */}
                    <IconButton
                      appearance="secondary"
                      aria-label={isPlaying ? 'Pause preview' : 'Play preview'}
                      className="h-48 w-48 shrink-0 rounded-full border border-border bg-bg-secondary"
                      disabled={!option.voice?.preview_audio}
                      shape="square"
                      size="xs"
                      variant="ghost"
                      onClick={(e) =>
                        handlePreviewClick(e, option.value, option.voice?.preview_audio)
                      }>
                      {isPlaying ? (
                        <PauseOutline height={24} width={24} />
                      ) : (
                        <PlayFill
                          className={isSelected ? 'text-text-primary' : 'text-icon-secondary'}
                          height={24}
                          width={24}
                        />
                      )}
                    </IconButton>
                    {/* Voice details */}
                    <Stack className="min-w-0 flex-1" direction="col" gap={2}>
                      <Typography className="font-medium" variant="body-short-02">
                        {option.label}
                      </Typography>
                      {createdByName ? (
                        <Typography color="text-text-secondary" variant="body-short-02">
                          {t('Course:AiVideoGeneration.CreatedBy', { name: createdByName })}
                        </Typography>
                      ) : null}
                    </Stack>
                    {/* Selected checkmark */}
                    {isSelected ? (
                      <CheckmarkFill
                        className="absolute right-12 top-12 text-text-primary"
                        height={18}
                        width={18}
                      />
                    ) : null}
                  </Stack>
                );
              })}
            </Stack>
          );
        }

        // Avatar + Voice mode: compact dropdown
        return (
          <Select
            disableClearable
            disabled={isVoicesFetching || voiceOptions.length === 0}
            getOptionLabel={getSelectOptionLabel}
            options={voiceOptions}
            renderOption={(option) => (
              <Stack alignItems="center" className="w-full gap-12 py-4" direction="row">
                <IconButton
                  appearance="secondary"
                  aria-label={playingVoiceId === option.value ? 'Pause preview' : 'Play preview'}
                  className="h-[4rem] w-[4rem] shrink-0 rounded-full border border-divider bg-bg-primary"
                  disabled={!option.voice?.preview_audio}
                  shape="square"
                  size="xs"
                  variant="ghost"
                  onClick={(e) => handlePreviewClick(e, option.value, option.voice?.preview_audio)}>
                  {playingVoiceId === option.value ? (
                    <PauseOutline className="h-16 w-16" />
                  ) : (
                    <PlayFill className="h-16 w-16" />
                  )}
                </IconButton>
                <Typography className="truncate font-medium" variant="body-long-02">
                  {option.label}
                </Typography>
              </Stack>
            )}
            size="large"
            value={resolvedValue}
            onChange={onChange}
          />
        );
      },
    },
    videoOrientation: {
      name: 'videoOrientation',
      label: t('Course:AiVideoGeneration.VideoOrientation'),
      required: true,
      type: FieldType.Custom,
      Component: ({ value, onChange }) => (
        <RadioGroup
          direction="row"
          gap={12}
          options={[
            {
              id: 'landscape',
              label: t('Course:AiVideoGeneration.Landscape'),
              Icon: MonitorOutline,
            },
            {
              id: 'portrait',
              label: t('Course:AiVideoGeneration.Portrait'),
              Icon: Mobile2Outline,
            },
          ]}
          selectedId={value}
          size="small"
          type="box"
          onChange={onChange}
        />
      ),
    },
    background: {
      name: 'background',
      label: t('Course:AiVideoGeneration.Background'),
      required: true,
      type: FieldType.Custom,
      Component: ({ value, onChange }) => (
        <Stack className="flex items-center gap-12" direction="row">
          {BACKGROUND_CONFIG.map((bg, index) => {
            const isSelected = value === bg.background;

            return (
              <Button
                key={bg.background}
                aria-label={`Background ${index + 1}`}
                className={cn('flex items-center justify-center rounded-full', {
                  'outline outline-2 outline-offset-2 outline-text-primary': isSelected,
                })}
                size="x-small"
                variant="ghost"
                onClick={() => onChange(bg.background)}>
                <span
                  className="h-34 w-34 rounded-full"
                  style={{ backgroundColor: bg.background }}
                />
              </Button>
            );
          })}
        </Stack>
      ),
    },
  };
};

const LAYOUT = ['preview', 'avatar', 'voice'];
const SHOW_VOICE_OVER_OPTION = false;

const INITIAL_VALUES: GenerateVideoFormValues = {
  avatar: '',
  background: BACKGROUND_CONFIG[0]?.background || '',
  language: '',
  voice: '',
  videoOrientation: 'landscape',
  videoType: 'avatarNarration',
  preview: '',
};

export const resolveGenerateVideoFormValues = (
  initialValues?: Partial<GenerateVideoFormValues>,
): GenerateVideoFormValues => {
  return {
    ...INITIAL_VALUES,
    ...initialValues,
  };
};

export const adaptFormValuesToVideoConfig = (
  formValues: GenerateVideoFormValues,
): Pick<VideoGenerationInputType, 'avatarId' | 'voiceId'> => {
  return {
    avatarId: formValues.avatar,
    voiceId: formValues.voice,
  };
};

interface GenerateVideoFormProps {
  onValuesChange: (values: GenerateVideoFormValues) => void;
  setSubmitRef: FormProps<GenerateVideoFormValues>['setSubmitRef'];
  onSubmit: (values: GenerateVideoFormValues) => void;
  initialValues?: Partial<GenerateVideoFormValues>;
  onAvatarSelectionChange?: (isSelecting: boolean) => void;
}

const GenerateVideoForm = (props: GenerateVideoFormProps) => {
  const { initialValues, onValuesChange, setSubmitRef, onSubmit, onAvatarSelectionChange } = props;
  const { t } = useTranslation('Course');

  const {
    avatars,
    fetching: isAvatarsFetching,
    defaultAvatarValue,
  } = useVideoCharacterAvatars({ filters: { providers: [VideoCharacterProviderEnum.Heygen] } });
  const { voices, fetching: isVoicesFetching } = useVideoCharacterVoices({
    filters: { providers: [VideoCharacterProviderEnum.Heygen] },
  });

  // Refs for cross-boundary communication with the avatar field Component
  const avatarChangeHandlerRef = React.useRef<((id: string) => void) | null>(null);
  const currentAvatarIdRef = React.useRef<string>('');

  const [showAvatarSelection, setShowAvatarSelection] = React.useState(false);

  const handleShowAvatarSelection = useCallback(() => {
    setShowAvatarSelection(true);
    onAvatarSelectionChange?.(true);
  }, [onAvatarSelectionChange]);

  const handleHideAvatarSelection = useCallback(() => {
    setShowAvatarSelection(false);
    onAvatarSelectionChange?.(false);
  }, [onAvatarSelectionChange]);

  const handleAvatarSelectedFromGrid = useCallback((avatarId: string) => {
    const avatarChangeHandler = avatarChangeHandlerRef.current;
    if (!avatarChangeHandler) {
      return;
    }

    avatarChangeHandler(avatarId);
    // Keep selection in sync immediately for grid highlight/read-after-select flows.
    currentAvatarIdRef.current = avatarId;
  }, []);

  const handleFormChange = useCallback<NonNullable<FormProps<GenerateVideoFormValues>['onChange']>>(
    (formik: any) => {
      onValuesChange(formik?.values as GenerateVideoFormValues);
    },
    [onValuesChange],
  );

  const initialFormValues = useMemo(() => {
    const preferredAvatar = initialValues?.avatar || '';
    const resolvedAvatar = avatars.some((avatar) => avatar.avatar_id === preferredAvatar)
      ? preferredAvatar
      : defaultAvatarValue;
    const resolvedVideoType = SHOW_VOICE_OVER_OPTION
      ? initialValues?.videoType || INITIAL_VALUES.videoType
      : 'avatarNarration';

    const preferredVoice = initialValues?.voice || '';
    const resolvedAvatarConfig = avatars.find((a) => a.avatar_id === resolvedAvatar);
    const resolvedVoice =
      resolvedVideoType === 'voiceOver'
        ? preferredVoice || voices[0]?.voice_id || ''
        : getDefaultVoice({
            voices,
            genderValue: resolvedAvatarConfig?.gender,
            preferredVoiceValue:
              preferredVoice || (resolvedAvatarConfig?.default_voice_id ?? undefined),
          });

    return {
      ...INITIAL_VALUES,
      ...initialValues,
      avatar: resolvedAvatar,
      voice: resolvedVoice,
    };
  }, [avatars, defaultAvatarValue, initialValues, voices]);

  const fields = useMemo(
    () =>
      getFormFields({
        t,
        avatars,
        voices,
        isAvatarsFetching,
        isVoicesFetching,
        onShowAvatarSelection: handleShowAvatarSelection,
        avatarChangeHandlerRef,
        currentAvatarIdRef,
      }),
    [avatars, handleShowAvatarSelection, isAvatarsFetching, isVoicesFetching, t, voices],
  );

  return (
    <Suspense fallback={<Loader className="mx-auto my-auto" />}>
      <AsyncView
        fetching={isAvatarsFetching || isVoicesFetching}
        isEmpty={false}
        renderData={() => (
          <>
            {showAvatarSelection ? (
              <AvatarGridView
                avatars={avatars}
                isLoading={isAvatarsFetching}
                selectedAvatarId={currentAvatarIdRef.current}
                onBack={handleHideAvatarSelection}
                onSelectAvatar={handleAvatarSelectedFromGrid}
              />
            ) : null}
            {/* Keep Form mounted to preserve formik context; hide it visually */}
            <Box className={showAvatarSelection ? 'hidden' : ''}>
              <Form<GenerateVideoFormValues>
                fields={fields}
                formName="VideoGenerationForm"
                initialValues={initialFormValues}
                layout={LAYOUT}
                setSubmitRef={setSubmitRef}
                // validate={validate}
                onChange={handleFormChange}
                onSubmit={onSubmit}
              />
            </Box>
          </>
        )}
        renderLoader={() => <Loader className="mx-auto my-auto" />}
      />
    </Suspense>
  );
};

export default GenerateVideoForm;
